import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Image, TouchableOpacity} from 'react-native';
import {
  Text, ActivityIndicator, Snackbar,
  Dialog, Portal, Button, TextInput,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import GradientHeader from '../../components/common/GradientHeader';
import {useNavigation} from '@react-navigation/native';
import {optionTypeApi} from '../../services/api/optionTypeApi';
import {colors, spacing} from '../../theme';

const GENDER_ICONS = {
  BOTH: {icon: 'account-group-outline', color: colors.primary, label: 'All'},
  FEMALE: {icon: 'gender-female', color: '#E91E63', label: 'Female'},
  MALE: {icon: 'gender-male', color: colors.info, label: 'Male'},
};

const MeasurementTypeListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();

  const [optionTypeId, setOptionTypeId] = useState(null);
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const [viewOption, setViewOption] = useState(null);
  const [viewVisible, setViewVisible] = useState(false);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [displayName, setDisplayName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await optionTypeApi.listConsolidatedOptionTypes(null, 'MEASUREMENT', true);
      const allOptionTypes = response.data.data;
      const mtType = allOptionTypes.find(ot => ot.code === 'MEASUREMENT_TYPE');
      if (mtType) {
        setOptionTypeId(mtType.id);
        setMeasurementTypes(mtType.options || []);
      }
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleToggle = useCallback(async (option, newValue) => {
    if (!optionTypeId) return;
    try {
      const response = await optionTypeApi.toggleOption(optionTypeId, option.id, {isEnabled: newValue});
      setMeasurementTypes(response.data.data.options || []);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [optionTypeId, t]);

  const openEditDialog = useCallback((option) => {
    setEditingOption(option);
    setDisplayName(option.name);
    setDialogVisible(true);
  }, []);

  const handleSaveOverride = useCallback(async () => {
    if (!editingOption || !optionTypeId) return;
    try {
      const response = await optionTypeApi.updateOptionOverride(optionTypeId, editingOption.id, {
        displayNameOverride: displayName.trim() || null,
      });
      setMeasurementTypes(response.data.data.options || []);
      setDialogVisible(false);
      setEditingOption(null);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [editingOption, optionTypeId, displayName, t]);

  const sortByEnabled = (a, b) => (b.enabled ? 1 : 0) - (a.enabled ? 1 : 0);
  const bothTypes = measurementTypes.filter(mt => mt.gender === 'BOTH').sort(sortByEnabled);
  const femaleTypes = measurementTypes.filter(mt => mt.gender === 'FEMALE').sort(sortByEnabled);
  const maleTypes = measurementTypes.filter(mt => mt.gender === 'MALE').sort(sortByEnabled);

  const renderOption = (option) => (
    <TouchableOpacity
      key={option.id}
      style={[styles.itemCard, !option.enabled && styles.itemCardDisabled]}
      activeOpacity={0.7}
      onPress={() => { setViewOption(option); setViewVisible(true); }}>
      <View style={styles.itemRow}>
        <TouchableOpacity
          onPress={() => handleToggle(option, !option.enabled)}
          hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
          <Icon
            name={option.enabled ? 'checkbox-marked-circle' : 'checkbox-blank-circle-outline'}
            size={22}
            color={option.enabled ? colors.primary : colors.textLight}
          />
        </TouchableOpacity>
        <View style={styles.itemContent}>
          <Text style={[styles.typeName, !option.enabled && styles.textDisabled]} numberOfLines={1}>{option.name}</Text>
          <Text style={styles.typeCode}>{option.code}</Text>
        </View>
        <TouchableOpacity
          onPress={() => openEditDialog(option)}
          hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
          style={styles.editBtn}>
          <Icon name="pencil-outline" size={16} color={colors.textSecondary} />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderSection = (genderKey, types) => {
    if (types.length === 0) return null;
    const genderInfo = GENDER_ICONS[genderKey];
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Icon name={genderInfo.icon} size={18} color={genderInfo.color} />
          <Text style={[styles.sectionTitle, {color: genderInfo.color}]}>{genderInfo.label}</Text>
          <Text style={styles.sectionCount}>{types.filter(t => t.enabled).length}/{types.length}</Text>
        </View>
        <View style={styles.itemGrid}>
          {types.map(renderOption)}
        </View>
      </View>
    );
  };

  if (isLoading && measurementTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader title={t('measurement.measurementTypes')} onBack={() => navigation.goBack()} />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        {renderSection('BOTH', bothTypes)}
        {renderSection('FEMALE', femaleTypes)}
        {renderSection('MALE', maleTypes)}
      </ScrollView>

      {/* View Modal */}
      <Portal>
        <Dialog visible={viewVisible} onDismiss={() => setViewVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{viewOption?.name}</Dialog.Title>
          <Dialog.Content>
            <Text style={styles.viewCode}>{viewOption?.code}</Text>
            <View style={styles.imageRow}>
              {viewOption?.imageUrl1 ? (
                <Image source={{uri: viewOption.imageUrl1}} style={styles.viewImage} />
              ) : <View style={styles.emptyImage} />}
              {viewOption?.imageUrl2 ? (
                <Image source={{uri: viewOption.imageUrl2}} style={styles.viewImage} />
              ) : <View style={styles.emptyImage} />}
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setViewVisible(false)}>{t('common.ok')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Edit Dialog */}
      <Portal>
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('optionType.editOption')}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('optionType.displayName')}
              value={displayName}
              onChangeText={setDisplayName}
              mode="outlined"
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>{t('common.cancel')}</Button>
            <Button mode="contained" onPress={handleSaveOverride} style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  scrollContent: {paddingBottom: 80},

  section: {
    marginBottom: spacing.md,
    paddingHorizontal: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    flex: 1,
  },
  sectionCount: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textSecondary,
    backgroundColor: colors.surface,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  itemGrid: {
    gap: spacing.sm,
  },
  itemCard: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  itemCardDisabled: {
    opacity: 0.45,
    borderColor: colors.divider,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  itemContent: {
    flex: 1,
  },
  typeName: {fontSize: 14, fontWeight: '600', color: colors.text},
  typeCode: {fontSize: 10, color: colors.textSecondary, marginTop: 2},
  textDisabled: {color: colors.textSecondary},
  editBtn: {
    padding: 4,
    backgroundColor: colors.background,
    borderRadius: 12,
  },

  viewCode: {color: colors.textSecondary, marginBottom: spacing.md},
  imageRow: {flexDirection: 'row', gap: spacing.md},
  viewImage: {width: 75, height: 75, borderRadius: 6, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background},
  emptyImage: {width: 75, height: 75, borderRadius: 6, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background},
  dialogInput: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  saveButton: {backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: 10},
});

export default MeasurementTypeListScreen;
